import { Store } from "./Store";

import { SessionStore } from "../uidispatcherapi/SessionStore";
import SimpleSocketListStore from "./SimpleSocketListStore";
import ShoppingListActions from "../actions/ShoppingListActions";
import ListAction from "../actions/ListAction";
import ListService from "../services/ListService";
import TrackingUtil from "../utils/TrackingUtil";
import ListPageUtils from "../utils/ListPageUtils";
import { ShoppingList } from "../types/ShoppingList";
import { DeleteShoppingListItem, ListToDeleteFrom } from "../types/ShoppingListItem";
import { ReplaceDto } from "../types/ReplaceDto";
import { ListSetBundleDto } from "../types/ListSetBundleDto";
import { PersonalListAddItem } from "../utils/PersonListUtils";
import { RECENTLY_BOUGHT_LISTID, CIP_LISTID, enhanceListWithCalculatedUniqAmount } from "../utils/ListOverviewUtils";
import { notify } from "../utils/Notifications";
import { sortingCriteriaPersonalLists } from "../utils/SortingCriteriaListsUtil";
import { isTier12Customer } from "./SessionStore";
import { shouldUseListOverviewScoring } from "../utils/FeatureToggleUtil";

export type SelectedArticlesChangeObject = {
    removeIds?: string[] | null;
    addIds?: string[] | null;
};

type ListState = {
    listId: string;
    list: ShoppingList | undefined | null;
    loading: boolean;
    error: boolean;
    isDeleted?: boolean | null;
    noListRefetching?: boolean;
    itemDeleteHappened?: boolean;
    itemsToChangeInSelectedArticles?: SelectedArticlesChangeObject;
};

const SCORING_KEY = "score_last_viewed_v1";

class ShoppingListStoreImpl extends Store {
    _loadedData!: {
        // FIXME(FD)
        [listId: string]: ListState;
    };
    _currentStoreId: string | undefined | null;
    _currentCustomerId: string | undefined | null;
    _allListsAreLoaded: boolean | undefined;
    _listsOrderedWithoutDynamics: string[] = [];

    constructor() {
        super();
        this.listenTo(SessionStore, this.onSessionStoreTrigger);
        this.listenTo(SimpleSocketListStore, this.onSimpleSocketListStoreTrigger);
        // FIXME(FD) get rid of actions
        this.listenTo(ListAction.create, this.handleCreateList as any);
        this.listenTo(ShoppingListActions.editList, this.handleEditList as any);
        this.listenTo(ShoppingListActions.deleteList, this.handleDeleteList as any);
        this.listenTo(ShoppingListActions.toggleCustomerVisible, this.handleToggleCustomerVisible as any);
        this.listenTo(ShoppingListActions.lockList, this.handleLockList as any);
        this.listenTo(ShoppingListActions.unlockList, this.handleUnlockList as any);
        this.listenTo(ShoppingListActions.deleteItem, this.handleDeleteItem as any);
        this.listenTo(ShoppingListActions.deleteMultipleItems, this.handleDeleteMultipleItems as any);
        this.listenTo(ShoppingListActions.replaceItem, this.handleReplaceItem as any);
        this.listenTo(ShoppingListActions.addItem, this.handleAddItem as any);
        this.listenTo(ShoppingListActions.setBundle, this.handleSetBundle as any);
        this.listenTo(ShoppingListActions.handleInteraction, this.handleInteraction as any);
        this.reset();
        this._currentStoreId = SessionStore.getCustomerStore();
        this._currentCustomerId = SessionStore.getCustomerId();
        this.fetchAllPersonalLists();
    }

    onSessionStoreTrigger() {
        const newStore = SessionStore.getCustomerStore();
        const storeChanged = this._currentStoreId !== newStore;
        const newCustomerId = SessionStore.getCustomerId();
        const customerChanged = this._currentCustomerId !== newCustomerId;
        if ((storeChanged && newStore) || (customerChanged && newCustomerId)) {
            this.reset();
            this._currentStoreId = newStore;
            this._currentCustomerId = newCustomerId;
            this.fetchAllPersonalLists();
            this.trigger();
        }
    }

    fetchAllPersonalLists() {
        const customerId = this._currentCustomerId;
        const storeId = this._currentStoreId;
        if (!customerId || !storeId || isTier12Customer()) {
            return;
        }
        const isEmployee = SessionStore.isEmployee();
        const country = SessionStore.getCountry();
        const useScoring = shouldUseListOverviewScoring();
        const scoringKey = useScoring ? SCORING_KEY : null;

        ListService.fetchAllPersonalLists(country, storeId, customerId, isEmployee, scoringKey)
            .then((response) => this.getPersonalListsCompleted(response))
            .catch((e) => this.handleSomethingFailed(e, "EX_GET_ALL_LISTS_FAILED", "Fetching list items failed."));

        ListService.getRecentlyBought(SessionStore.getCountry(), storeId, customerId)
            .then((response) => this.getListSuccessfully(response, RECENTLY_BOUGHT_LISTID))
            .catch((e) => this.getListFailed(e, RECENTLY_BOUGHT_LISTID));

        ListService.getCIP(SessionStore.getCountry(), storeId, customerId)
            .then((response) => this.getListSuccessfully(response, CIP_LISTID))
            .catch((e) => this.getListFailed(e, CIP_LISTID));
    }

    getPersonalListsCompleted(response: { lists: ShoppingList[] }) {
        const lists = response.lists;
        this.setPersonalLists(lists);
    }

    setPersonalLists(lists: ShoppingList[]) {
        const filteredLists = (lists ?? []).filter((list) => {
            return list.storeId === this._currentStoreId || list.globallyVisible;
        });
        this._listsOrderedWithoutDynamics = (filteredLists ?? []).map((list) => list.listId);
        (filteredLists ?? []).forEach((list) => this.getListSuccessfully(list, list.listId));
        this._allListsAreLoaded = true;
        this.trigger();
    }

    handleCreateList(
        name: string,
        globallyVisible: boolean,
        articles: PersonalListAddItem[] | undefined | null,
        readOnlyForEmps?: boolean
    ) {
        const storeId = this._currentStoreId;
        if (!storeId) {
            return;
        }

        const readOnlyForEmployees = readOnlyForEmps || false;
        const body = {
            userId: this._currentCustomerId,
            storeId: storeId,
            name,
            globallyVisible,
            readOnlyForEmployees,
            items: [] as any
        };
        if (articles) {
            body.items = articles.map((item) => {
                return {
                    articleVariantId: item.articleVariantId,
                    bundleId: item.bettyBundleId,
                    bettyBundleId: item.bettyBundleId
                };
            });
        }
        const country = SessionStore.getCountry();

        ListService.createList(country, body)
            .then((response) => this.updateSuccessListState(response, "EX_CREATE_LIST_SUCCESS"))
            .catch((error) =>
                this.handleSomethingFailed(
                    error,
                    ListPageUtils.getKeyCreateListError(error),
                    "The list couldn't be created."
                )
            );
    }

    handleSetBundle(replace: ListSetBundleDto) {
        const body = {
            oldBettyBundleId: replace.oldBettyBundleId,
            newBettyBundleId: replace.newBettyBundleId,
            bundleId: replace.newBundleId ? replace.newBundleId : replace.bundleId
        };
        const removeIds = replace.oldBettyBundleId ? [replace.oldBettyBundleId] : null;
        const addIds = replace.newBettyBundleId ? [replace.newBettyBundleId] : null;

        ListService.setBundle(
            SessionStore.getCountry(),
            this._currentCustomerId,
            replace.listId,
            body,
            this._currentStoreId
        )
            .then((response) =>
                this.updateSuccessListState(response, null, true, null, {
                    removeIds: removeIds,
                    addIds: addIds
                })
            )
            .catch((e) => TrackingUtil.trackUiErrorCa("ShoppingListStore", "Setting bundle in list failed: ", e));
    }

    addBatchOfArticlesToList(list: ShoppingList, items: PersonalListAddItem[]) {
        const body = {
            items: items
        };
        const country = SessionStore.getCountry();

        ListService.addToList(country, this._currentCustomerId, list.listId, body, this._currentStoreId)
            .then((response) => this.updateSuccessListState(response, "EX_ADD_TO_LIST_SUCCESS"))
            .catch((e) =>
                this.handleSomethingFailed(e, ListPageUtils.getKeyAddItemsError(e), "Adding batch of items failed.")
            );
    }

    handleAddItem(list: ShoppingList, bettyVariantId: string, bettyBundleId: string) {
        const items = [
            {
                articleVariantId: bettyVariantId,
                bundleId: bettyBundleId,
                bettyBundleId
            }
        ];
        this.addBatchOfArticlesToList(list, items);
    }

    handleReplaceItem(replace: ReplaceDto) {
        const variantId = replace.bettyVariantId;
        const bundleId = replace.newBundleId.bundleNumber;
        const bettyBundleId = replace.newBundleId.bettyBundleId;
        const body = {
            oldItem: {
                articleVariantId: replace.oldVariantId,
                bundleId: replace.oldBundleId,
                bettyBundleId: replace.bettyBundleId
            },
            newItem: {
                articleVariantId: variantId,
                bundleId,
                bettyBundleId,
                amount: replace.amount
            }
        };
        const country = SessionStore.getCountry();
        ListService.replaceItem(country, this._currentCustomerId, replace.listId, body, this._currentStoreId)
            .then((response) =>
                this.updateSuccessListState(response, "EX_LIST_REPLACE_ITEM_SUCCESS", true, null, {
                    removeIds: [replace.bettyBundleId]
                })
            )
            .catch((e) => this.handleSomethingFailed(e, "EX_LIST_REPLACE_ITEM_FAILED", "replace item failed."));
    }

    handleDeleteMultipleItems(items: DeleteShoppingListItem[], listId: string | undefined | null) {
        const country = SessionStore.getCountry();
        const removeItems = { items: items };
        const filteredIds = (items?? [])
            .map((item) => item.parentBettyBundleId || item.bettyBundleId)
            .filter((item): item is string => item !== undefined && item !== null);
        const removeIds = filteredIds.length > 0 ? filteredIds : null;
        const successMessage = items.length > 1 ? "EX_LIST_DELETE_ITEMS_SUCCESS" : "EX_LIST_DELETE_ITEM_SUCCESS";
        const failMessage = items.length > 1 ? "EX_LIST_DELETE_ITEMS_FAILED" : "EX_LIST_DELETE_ITEM_FAILED";

        ListService.deleteItems(country, this._currentCustomerId, listId, removeItems, this._currentStoreId)
            .then((response) =>
                this.updateSuccessListState(response, successMessage, true, true, {
                    removeIds: removeIds
                })
            )
            .catch((e) => this.handleSomethingFailed(e, failMessage, "Deleting list item(s) failed."));
    }

    handleDeleteItem(item: DeleteShoppingListItem, listToDeleteFrom: ListToDeleteFrom) {
        this.handleDeleteMultipleItems([item], listToDeleteFrom.listId);
    }

    handleInteraction(listId: string) {
        const country = SessionStore.getCountry();
        if (this._currentCustomerId && SessionStore.isCustomer()) {
            ListService.updateLastInteraction(country, this._currentCustomerId, listId)
                .then((response) => this.getListSuccessfully(response, listId))
                .catch((e) =>
                    this.handleSomethingFailed(e, null, "Updating last interaction failed! Id: " + listId, true)
                );
        }
    }

    handleUnlockList(item: ShoppingList) {
        const country = SessionStore.getCountry();
        ListService.unlockList(country, this._currentCustomerId, item.listId, this._currentStoreId)
            .then((response) => this.updateSuccessListState(response, null))
            .catch((e) =>
                this.handleSomethingFailed(
                    e,
                    "EX_LIST_UNLOCK_LIST_FAILED",
                    "Unlocking of list failed! Id: " + item.listId
                )
            );
    }

    handleLockList(item: ShoppingList) {
        const country = SessionStore.getCountry();
        ListService.lockList(country, this._currentCustomerId, item.listId, this._currentStoreId)
            .then((response) => this.updateSuccessListState(response, null))
            .catch((e) =>
                this.handleSomethingFailed(e, "EX_LIST_LOCK_LIST_FAILED", "Locking of list failed! Id: " + item.listId)
            );
    }

    handleToggleCustomerVisible(list: ShoppingList) {
        const country = SessionStore.getCountry();
        ListService.setCustomerVisible(country, list.listId, !list.visibleForCustomer)
            .then((response) => this.updateSuccessListState(response, null))
            .catch((e) =>
                this.handleSomethingFailed(
                    e,
                    "EX_LIST_SET_VISIBILITY_FAILED",
                    "Setting customer-visibility of list failed! Id: " + list.listId
                )
            );
    }

    handleDeleteList(item: ShoppingList) {
        const country = SessionStore.getCountry();
        ListService.deleteList(country, this._currentCustomerId, item.listId, this._currentStoreId)
            .then(() => this.removeList(item.listId))
            .catch((e) =>
                this.handleSomethingFailed(
                    e,
                    "EX_LIST_DELETE_LIST_FAILED",
                    "Deletion of list failed! Id: " + item.listId
                )
            );
    }

    handleEditList(
        list: ShoppingList,
        name: string,
        globallyVisible: boolean,
        readOnlyForEmployees: boolean,
        showNoMessage?: boolean
    ) {
        const country = SessionStore.getCountry();
        const body = {
            userId: this._currentCustomerId,
            storeId: this._currentStoreId,
            name,
            readOnlyForEmployees,
            globallyVisible
        };

        ListService.editListMetadata(country, this._currentCustomerId, list.listId, body, this._currentStoreId)
            .then((response) => this.updateSuccessListState(response))
            .catch((e) => this.handleSomethingFailed(e, "EX_EDIT_LIST_FAILED", "Edit list failed.", showNoMessage));
    }

    handleSomethingFailed(
        e: any,
        phraseAppKey: string | undefined | null,
        errorMsg: string | undefined | null,
        showNoMessage?: boolean | undefined | null
    ) {
        if (phraseAppKey && (showNoMessage === undefined || !showNoMessage)) {
            const notification = {
                type: "warning" as const,
                key: phraseAppKey,
                values: [],
                id: phraseAppKey,
                timeout: 5000
            };
            notify(notification);
        }
        if (errorMsg) {
            TrackingUtil.trackUiErrorCa("ShoppingListStore", errorMsg, e);
        }
    }

    updateSuccessListState(
        updatedList: ShoppingList,
        phraseAppKey?: string | undefined | null,
        noListRefetching?: boolean,
        itemDeleteHappened?: boolean | undefined | null,
        itemsToChangeInSelectedArticles?: SelectedArticlesChangeObject
    ) {
        if (phraseAppKey) {
            const notification = {
                type: "success" as const,
                key: phraseAppKey,
                values: [],
                id: phraseAppKey,
                timeout: 5000
            };
            notify(notification);
        }
        this._loadedData[updatedList.listId] = {
            loading: false,
            error: false,
            list: enhanceListWithCalculatedUniqAmount(updatedList),
            listId: updatedList.listId,
            noListRefetching: noListRefetching || false,
            itemDeleteHappened: itemDeleteHappened || false
        };
        const dataToTriggerIncludingCheckboxChanges = Object.assign({}, this._loadedData[updatedList.listId], {
            itemsToChangeInSelectedArticles: itemsToChangeInSelectedArticles
        });
        if (!this._listsOrderedWithoutDynamics.includes(updatedList.listId)) {
            this._listsOrderedWithoutDynamics.unshift(updatedList.listId);
        }
        this.trigger(dataToTriggerIncludingCheckboxChanges);
    }

    getListSuccessfully(response: ShoppingList, listId: string) {
        const currentList = this._loadedData[listId] || {
            listId
        };
        currentList.list = ListPageUtils.isDynamicList(listId)
            ? Object.assign({}, response, { listId })
            : enhanceListWithCalculatedUniqAmount(response);
        currentList.loading = false;
        currentList.error = false;
        this._loadedData[listId] = currentList;
        this.trigger(currentList);
    }

    getListFailed(e: any, listId: string) {
        const newState = {
            listId,
            list: null,
            loading: false,
            error: true
        };
        this._loadedData[listId] = newState;
        this.handleSomethingFailed(e, null, "Loading of list failed.");
        this.trigger(newState);
    }

    onSimpleSocketListStoreTrigger({
        listId,
        listUpdateFn,
        newLists
    }: {
        listId?: string;
        listUpdateFn?: (list: ShoppingList | undefined | null) => {
            list: ShoppingList | undefined | null;
            isDeleted: boolean;
            isNew: boolean;
        };
        newLists?: ShoppingList[];
    }) {
        if (listId && listUpdateFn) {
            const listData = this._loadedData[listId];
            const oldList = listData?.list;
            const listUpdate = listUpdateFn(oldList);
            const isNew = listUpdate.isNew;
            const isDeleted = listUpdate.isDeleted;
            const updatedList = listUpdate.list;
            if (isNew) {
                this._loadedData[listId] = {
                    listId,
                    list: updatedList,
                    loading: false,
                    error: false
                };
                if (!this._listsOrderedWithoutDynamics.includes(listId)) {
                    this._listsOrderedWithoutDynamics.unshift(listId);
                }
                this.trigger(this._loadedData[listId]);
            } else if (isDeleted) {
                this.removeList(listId);
            } else {
                const currentListState = this._loadedData[listId] || {};
                this._loadedData[listId] = Object.assign({}, currentListState, {
                    list: updatedList
                });
                this.trigger(this._loadedData[listId]);
            }
        } else if (newLists) {
            this.setPersonalLists(newLists);
        }
    }

    changeSelectedBundles(listId: string, ids: string[], isSelect: boolean) {
        const itemsToChange = isSelect ? { addIds: ids } : { removeIds: ids };
        const list = this._loadedData[listId].list;
        if (list) {
            this.updateSuccessListState(list, null, true, null, itemsToChange);
        }
    }

    removeList(listId: string) {
        this._loadedData[listId] = Object.assign({}, this._loadedData[listId], {
            isDeleted: true,
            list: null
        });
        this.trigger(this._loadedData[listId]);
    }

    getListData(listId: string) {
        if (this._allListsAreLoaded) {
            //e.g. list belongs to different customer
            const notFoundState = {
                listId,
                list: null,
                loading: false,
                error: true,
                selectedBundleIds: []
            };
            return this._loadedData[listId] ? this._loadedData[listId] : notFoundState;
        }
        return this._loadedData[listId] ? this._loadedData[listId] : undefined;
    }

    getAllListsWithoutDynamicLists(): ShoppingList[] {
        const listsOrdered: (ShoppingList | undefined | null)[] = this._listsOrderedWithoutDynamics.map(
            (id) => this._loadedData[id]?.list
        );
        const listsOrderedWithoutNulls: ShoppingList[] = listsOrdered.filter(
            (list): list is ShoppingList => list !== null && list !== undefined
        );
        if (shouldUseListOverviewScoring()) {
            return listsOrderedWithoutNulls;
        } else {
            return listsOrderedWithoutNulls.sort((a, b) => {
                const order = sortingCriteriaPersonalLists.ordering;
                const sortFn = sortingCriteriaPersonalLists.sortFn;

                for (let i = 0; i < sortFn.length; i++) {
                    const aValue = sortFn[i](a);
                    const bValue = sortFn[i](b);
                    const ordering = order[i];

                    let comparison = 0;
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        comparison = aValue.localeCompare(bValue);
                    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                        comparison = aValue - bValue;
                    } else {
                        comparison = String(aValue).localeCompare(String(bValue));
                    }

                    if (comparison !== 0) {
                        return ordering === 'asc' ? comparison : -comparison;
                    }
                }
                return 0;
            });
        }
    }

    getAllListsAreLoaded() {
        return this._allListsAreLoaded;
    }

    reset() {
        this._currentStoreId = null;
        this._currentCustomerId = null;
        this._loadedData = {};
        this._allListsAreLoaded = false;
        this._listsOrderedWithoutDynamics = [];
    }
}

export default new ShoppingListStoreImpl();
export type ShoppingListState = ListState;
